import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IInitialState } from '../../../../reduxService';
import * as BeMaccaferriStoreHelper from '../../../api/store/BeMaccaferriStoreHelper';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import { IMacro1ProjectPutData } from '../../../api/IApiRequest';
import { IMacro1ProjectGetData } from '../../../api/IApiResponse';
import { IRockSlopeForm, RockSlopeDefault } from '../../components/InputForm/RockSlope/RockSlope';
import { IAnchorBarForm, AnchorBarDefault } from '../../components/InputForm/AnchorBar/AnchorBar';
import { IMeshForm, MeshDefault } from '../../components/InputForm/Mesh/Mesh';
import { ISafetyCoefficientsForm, SafetyCoefficientsDefault } from '../../components/InputForm/SafetyCoefficients/SafetyCoefficients';
import { convertTo, EConversionSIToImperial } from '../../../conversion';

const checkConversion = (unit: string | undefined, value: number, conversion: EConversionSIToImperial) => {
    return unit == 'Metric' ? value : convertTo(value, conversion);
}

export const projectToData = (data: IProjectMacro1WithInfo): IMacro1ProjectPutData => {
    return {
        id: data.projectId ?? data.projectInfo?.projectId,
        siteEnvironmentalLevelId: data.siteEnvironmentalLevelId,
        unit: data.projectInfo?.unit,
        rockSlope: {
            inclinationOfTheSlope: Number(data.rockSlope?.inclination),
            unlockSlopeAndAnchorAngleRelationLimit: data.rockSlope?.slopeAnchorAngleLimitUnlocked,
            thicknessOfTheSurficialInstability: Number(data.rockSlope?.thicknessSurficialInstability),
            densityOfTheRock: Number(data.rockSlope?.rockDensity),
            dipOfTheSetOfJoints: Number(data.rockSlope?.dipSetJoints),
            compressiveStrengthOfTheCriticalJoint: Number(data.rockSlope?.compressiveStrengthCriticalJoint),
            roughnessCoefficientOfTheCriticalJoint: Number(data.rockSlope?.roughnessCoefficientCriticalJoint),
            horizontalSeismicCoefficientCH: Number(data.rockSlope?.horizontalSeismicCoefficient),
            verticalSeismicCoefficientCV: Number(data.rockSlope?.verticalSeismicCoefficient),
            externalForce: Number(data.rockSlope?.externalForce),
            angleBetweenTheForceAndTheSlope: Number(data.rockSlope?.angleForceSlope),
        },
        anchorBars: {
            horizontalSpacingBetweenTheAnchors: Number(data.anchorBar?.horizontalSpacing),
            verticalSpacingBetweenTheAnchors: Number(data.anchorBar?.verticalSpacing),
            inclinationOfBarToTheHorizontal: Number(data.anchorBar?.inclination),
            coefficient: Number(data.anchorBar?.coefficient),
            isCustom: data.anchorBar?.customAnchorType,
            anchorTypeId: data.anchorBar?.anchorType,
            anchorTypeName: data.anchorBar?.anchorTypeName,
            innerDiameterOfTheAnchorBar: Number(data.anchorBar?.innerDiameter),
            externalDiameterOfTheAnchorBar: Number(data.anchorBar?.externalDiameter),
            drillingDiameter: Number(data.anchorBar?.drillingDiameter),
            yieldTensileStressOfTheAnchor: Number(data.anchorBar?.yieldTensileStress),
            corrosionCrownKind: data.anchorBar?.automaticThickness ? "Automatic" : "Custom",
            customThickness: Number(data.anchorBar?.customThickness),
            environmentAggressivity: data.anchorBar?.environmentalAgressivity,
            lifespan: Number(data.anchorBar?.lifespan),
            nfP94270: data.anchorBar?.NFP94270,
            bondStressBetweenGroutAndRock: Number(data.anchorBar?.bondStress),
            assumedPlasticizationBetweenRockAndAnchor: Number(data.anchorBar?.assumedPlasticization)
        },
        mesh: {
            meshTypeId: data.mesh?.meshType,
            meshTypeName: data.mesh?.meshTypeName,
            nominalPunchingResistanceInLabTest: Number(data.mesh?.nominalPunchingResistance),
            //ultimateTensileStrengthPerLinearMeter: data.mesh?.ultimateTensileStrengthPerLinearMeter,
        },
        safetyCoefficients: {
            uncertaintyOfTheThicknessOfSurficialInstability: Number(data.safetyCoefficients?.uncertaintyThicknessSurficialInstability),
            uncertaintyOfTheRockMassUnitWeight: Number(data.safetyCoefficients?.uncertaintyRockMassUnitWeight),
            uncertaintyOfRockBehaviorAndWeathering: Number(data.safetyCoefficients?.uncertaintyRockBehaviorAndWeathering),
            isCustomRed: data.safetyCoefficients?.safetyCoefficientReduceStabilizingForceCustom,
            safetyCoefficientToReduceStabilizingForcesCustom: Number(data.safetyCoefficients?.gR),
            slopeSurfaceMorphology: Number(data.safetyCoefficients?.slopeSurfaceMorphology),
            externalLoads: Number(data.safetyCoefficients?.externalLoads),
            isCustomInc: data.safetyCoefficients?.safetyCoefficientIncreaseDrivingForceCustom,
            safetyCoefficientToIncreaseTheDrivingForcesCustom: Number(data.safetyCoefficients?.yD),
            globalSafetyCoefficient: Number(data.safetyCoefficients?.yDF),
            coefficientAppliedOnTheTensileAndThePunchResistance: Number(data.safetyCoefficients?.coefficientPunchResistance),
            coefficientAppliedOnTheyieldStressOfTheBar: Number(data.safetyCoefficients?.coefficientYieldStressBar),
            coefficientAppliedOnTheBondStress: Number(data.safetyCoefficients?.coefficientBondStress),
        }
    };
}

export const dataToProject = (data: IMacro1ProjectGetData): IProjectMacro1WithInfo => {
    const pUnit = data?.projectInfos?.unit ?? 'Metric';
    //console.log(pUnit, data.projectInfos);                                              
    return {
        siteEnvironmentalLevelId: data.siteEnvironmentalLevelId ?? '',
        projectInfo: {
            projectId: data.projectId ?? '',
            projectName: data.projectInfos?.projectName ?? '',
            nrProject: data.projectInfos?.nrProject ?? '0',
            unit: data.projectInfos?.unit ?? 'Metric',
            projectType: data.projectInfos?.projectType ?? 'macro1',
            owner: data.projectInfos?.owner ?? '',
            designerFirmName: data.projectInfos?.designerFirmName ?? '',
            designerFirmCountry: data.projectInfos?.designerFirmCountry ?? '',
            designerFirmCity: data.projectInfos?.designerFirmCity ?? '',
            coordinates: data.projectInfos?.coordinates ?? [undefined, undefined],
            productCountry: data.projectInfos?.productCountry ?? null,
            notes: data.projectInfos?.notes ?? '',
            mapImage: data.projectInfos?.mapImage ?? '',
        },
        readOnly: data.readOnly,
        hasBeenRan: data.hasBeenRan,
        rockSlope: {
            inclination: data.rockSlopeDatas?.inclinationOfTheSlope ?? RockSlopeDefault.inclination,
            slopeAnchorAngleLimitUnlocked: data.rockSlopeDatas?.unlockSlopeAndAnchorAngleRelationLimit ?? RockSlopeDefault.slopeAnchorAngleLimitUnlocked,
            thicknessSurficialInstability: data.rockSlopeDatas?.thicknessOfTheSurficialInstability ?? checkConversion(pUnit, RockSlopeDefault.thicknessSurficialInstability, EConversionSIToImperial.m_to_ft),
            rockDensity: data.rockSlopeDatas?.densityOfTheRock ?? checkConversion(pUnit, RockSlopeDefault.rockDensity, EConversionSIToImperial.kNm3_to_lbfft3),
            dipSetJoints: data.rockSlopeDatas?.dipOfTheSetOfJoints ?? RockSlopeDefault.dipSetJoints,
            compressiveStrengthCriticalJoint: data.rockSlopeDatas?.compressiveStrengthOfTheCriticalJoint ?? RockSlopeDefault.compressiveStrengthCriticalJoint,
            roughnessCoefficientCriticalJoint: data.rockSlopeDatas?.roughnessCoefficientOfTheCriticalJoint ?? RockSlopeDefault.roughnessCoefficientCriticalJoint,
            horizontalSeismicCoefficient: data.rockSlopeDatas?.horizontalSeismicCoefficientCH ?? RockSlopeDefault.horizontalSeismicCoefficient,
            verticalSeismicCoefficient: data.rockSlopeDatas?.verticalSeismicCoefficientCV ?? RockSlopeDefault.verticalSeismicCoefficient,
            externalForce: data.rockSlopeDatas?.externalForce ?? checkConversion(pUnit, RockSlopeDefault.externalForce, EConversionSIToImperial.kN_to_lbf),
            angleForceSlope: data.rockSlopeDatas?.angleBetweenTheForceAndTheSlope ?? RockSlopeDefault.angleForceSlope,
        },
        anchorBar: {
            horizontalSpacing: data.anchorBarsDatas?.horizontalSpacingBetweenTheAnchors ?? checkConversion(pUnit, AnchorBarDefault.horizontalSpacing, EConversionSIToImperial.m_to_ft),
            verticalSpacing: data.anchorBarsDatas?.verticalSpacingBetweenTheAnchors ?? checkConversion(pUnit, AnchorBarDefault.verticalSpacing, EConversionSIToImperial.m_to_ft),
            inclination: data.anchorBarsDatas?.inclinationOfBarToTheHorizontal ?? AnchorBarDefault.inclination,
            coefficient: data.anchorBarsDatas?.coefficient ?? AnchorBarDefault.coefficient,
            customAnchorType: data.anchorBarsDatas?.isCustom ?? AnchorBarDefault.customAnchorType,
            anchorType: data.anchorBarsDatas?.anchorTypeId ?? AnchorBarDefault.anchorType,
            anchorTypeName: data.anchorBarsDatas?.anchorTypeName ?? AnchorBarDefault.anchorTypeName,
            innerDiameter: data.anchorBarsDatas?.innerDiameterOfTheAnchorBar ?? checkConversion(pUnit, AnchorBarDefault.innerDiameter, EConversionSIToImperial.mm_to_in),
            externalDiameter: (data.anchorBarsDatas?.externalDiameterOfTheAnchorBar ?? checkConversion(pUnit, Number(AnchorBarDefault.externalDiameter), EConversionSIToImperial.mm_to_in)).toFixed(2),
            drillingDiameter: data.anchorBarsDatas?.drillingDiameter ?? checkConversion(pUnit, AnchorBarDefault.drillingDiameter, EConversionSIToImperial.mm_to_in),
            yieldTensileStress: data.anchorBarsDatas?.yieldTensileStressOfTheAnchor ??
                (pUnit === 'Metric'
                    ? checkConversion(pUnit, AnchorBarDefault.yieldTensileStress, EConversionSIToImperial.MPa_to_ksi)
                : Number(checkConversion(pUnit, AnchorBarDefault.yieldTensileStress, EConversionSIToImperial.MPa_to_ksi).toFixed(0))),

            automaticThickness: data.anchorBarsDatas?.corrosionCrownKind == "Automatic" ?? AnchorBarDefault.automaticThickness,
            customThickness: data.anchorBarsDatas?.customThickness ?? checkConversion(pUnit, AnchorBarDefault.customThickness, EConversionSIToImperial.mm_to_in),
            environmentalAgressivity: data.anchorBarsDatas?.environmentAggressivity ?? AnchorBarDefault.environmentalAgressivity,
            lifespan: data.anchorBarsDatas?.lifespan ?? AnchorBarDefault.lifespan,
            NFP94270: data.anchorBarsDatas?.nfP94270 ?? AnchorBarDefault.NFP94270,
            bondStress: data.anchorBarsDatas?.bondStressBetweenGroutAndRock ?? checkConversion(pUnit, AnchorBarDefault.bondStress, EConversionSIToImperial.MPa_to_psi),
            assumedPlasticization: data.anchorBarsDatas?.assumedPlasticizationBetweenRockAndAnchor ?? checkConversion(pUnit, AnchorBarDefault.assumedPlasticization, EConversionSIToImperial.m_to_ft),
        },
        mesh: {
            meshType: data.meshDatas?.meshTypeId ?? MeshDefault.meshType,
            meshTypeName: data.meshDatas?.meshTypeName ?? MeshDefault.meshTypeName,
            nominalPunchingResistance: data.meshDatas?.nominalPunchingResistanceInLabTest ?? checkConversion(pUnit, MeshDefault.nominalPunchingResistance, EConversionSIToImperial.kN_to_lbf),
            //ultimateTensileStrengthPerLinearMeter: data.meshDatas?.ultimateTensileStrengthPerLinearMeter ?? checkConversion(pUnit, MeshDefault.ultimateTensileStrengthPerLinearMeter, EConversionSIToImperial.kNm_to_lbfft),
        },
        safetyCoefficients: {
            uncertaintyThicknessSurficialInstability: data.safetyCoefficientsDatas?.uncertaintyOfTheThicknessOfSurficialInstability ?? SafetyCoefficientsDefault.uncertaintyThicknessSurficialInstability,
            uncertaintyRockMassUnitWeight: data.safetyCoefficientsDatas?.uncertaintyOfTheRockMassUnitWeight ?? SafetyCoefficientsDefault.uncertaintyRockMassUnitWeight,
            uncertaintyRockBehaviorAndWeathering: data.safetyCoefficientsDatas?.uncertaintyOfRockBehaviorAndWeathering ?? SafetyCoefficientsDefault.uncertaintyRockBehaviorAndWeathering,
            safetyCoefficientReduceStabilizingForceCustom: data.safetyCoefficientsDatas?.isCustomRed ?? SafetyCoefficientsDefault.safetyCoefficientReduceStabilizingForceCustom,
            gR: data.safetyCoefficientsDatas?.safetyCoefficientToReduceStabilizingForcesCustom ?? SafetyCoefficientsDefault.gR,
            slopeSurfaceMorphology: data.safetyCoefficientsDatas?.slopeSurfaceMorphology ?? SafetyCoefficientsDefault.slopeSurfaceMorphology,
            externalLoads: data.safetyCoefficientsDatas?.externalLoads ?? SafetyCoefficientsDefault.externalLoads,
            safetyCoefficientIncreaseDrivingForceCustom: data.safetyCoefficientsDatas?.isCustomInc ?? SafetyCoefficientsDefault.safetyCoefficientIncreaseDrivingForceCustom,
            yD: data.safetyCoefficientsDatas?.safetyCoefficientToIncreaseTheDrivingForcesCustom ?? SafetyCoefficientsDefault.yD,
            yDF: data.safetyCoefficientsDatas?.globalSafetyCoefficient ?? SafetyCoefficientsDefault.yDF,
            coefficientPunchResistance: data.safetyCoefficientsDatas?.coefficientAppliedOnTheTensileAndThePunchResistance ?? SafetyCoefficientsDefault.coefficientPunchResistance,
            coefficientYieldStressBar: data.safetyCoefficientsDatas?.coefficientAppliedOnTheyieldStressOfTheBar ?? SafetyCoefficientsDefault.coefficientYieldStressBar,
            coefficientBondStress: data.safetyCoefficientsDatas?.coefficientAppliedOnTheBondStress ?? SafetyCoefficientsDefault.coefficientBondStress,
        },
    }
}

export interface IProjectMacro1 {
    rockSlope?: IRockSlopeForm;
    anchorBar?: IAnchorBarForm;
    mesh?: IMeshForm;
    safetyCoefficients?: ISafetyCoefficientsForm;
    siteEnvironmentalLevelId?: string;
}

export interface IProjectMacro1WithInfo extends IProjectMacro1 {
    projectId?: string;
    id?: string;
    projectInfo?: IProjectInfoForm;
    readOnly?: boolean;
    hasBeenRan?: boolean;
    disableRun?: boolean;
}

const initialState: IInitialState<IProjectMacro1WithInfo> = {
    initialState: true,
};

export const projectSlice = createSlice({
    name: 'projectMacro1',
    initialState,
    reducers: {
        cleanProject: state => {
            state.initialState = true;
            state.data = undefined;
        },
        setProject: (state, action: PayloadAction<IProjectMacro1WithInfo>) => {
            state.initialState = false;
            state.data = { ...state.data, ...action.payload } as IProjectMacro1WithInfo;
        },
        setProjectInfo: (state, action: PayloadAction<IProjectInfoForm>) => {
            state.initialState = false;
            state.data = { ...state.data, projectInfo: action.payload } as IProjectMacro1WithInfo;
        },
        setRockSlope: (state, action: PayloadAction<IRockSlopeForm>) => {
            state.initialState = false;
            state.data = { ...state.data, rockSlope: action.payload };
        },
        setAnchorBar: (state, action: PayloadAction<IAnchorBarForm>) => {
            state.initialState = false;
            state.data = { ...state.data, anchorBar: action.payload };
        },
        setMesh: (state, action: PayloadAction<IMeshForm>) => {
            state.initialState = false;
            state.data = { ...state.data, mesh: action.payload };
        },
        setSiteEnvLevel: (state, action: PayloadAction<string>) => {
            state.initialState = false;
            state.data = { ...state.data, siteEnvironmentalLevelId: action.payload };
        },
        setSafetyCoefficients: (state, action: PayloadAction<ISafetyCoefficientsForm>) => {
            state.initialState = false;
            state.data = { ...state.data, safetyCoefficients: action.payload };
        },
        setReadOnly: (state, action: PayloadAction<boolean>) => {
            state.initialState = false;
            state.data = { ...state.data, readOnly: action.payload } as IProjectMacro1WithInfo;
        },
        setHasBeenRan: (state, action: PayloadAction<boolean>) => {
            state.initialState = false;
            state.data = { ...state.data, hasBeenRan: action.payload } as IProjectMacro1WithInfo;
        },
        setDisableRun: (state, action: PayloadAction<boolean>) => {
            state.initialState = false;
            state.data = { ...state.data, disableRun: action.payload } as IProjectMacro1WithInfo;
        },
    }
});

export const committedProjectSlice = createSlice({
    name: 'committedProjectMacro1',
    initialState,
    reducers: {
        cleanCommittedProject: state => {
            state.initialState = true;
            state.data = undefined;
        },
        commitProject: (state, project: PayloadAction<IProjectMacro1WithInfo>) => {
            //console.log('commitProject');
            state.data = { ...project.payload };
        }
    },
});

export const { setProject, cleanProject, setProjectInfo, setRockSlope, setAnchorBar, setMesh, setSafetyCoefficients, setSiteEnvLevel, setReadOnly, setHasBeenRan, setDisableRun } =
    projectSlice.actions;

export const { cleanCommittedProject, commitProject } = committedProjectSlice.actions;

export default projectSlice.reducer;
